import React, { useState } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../context/AuthContext'
import { useQRLocator } from '../../hooks/useQRLocator'
import QRScannerModal from '../../components/admin/QRScannerModal'
import ManualQRInput from '../../components/admin/ManualQRInput'
import QRLocatorResult from '../../components/admin/QRLocatorResult'

const QRLocalizadorPage: React.FC = () => {
  const { tienePermiso } = useAuth()
  const { colors } = useTheme()
  const { isLocating, locatorResult, locateQR, clearResult } = useQRLocator()
  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const [isManualInputOpen, setIsManualInputOpen] = useState(false)

  // Verificar permiso
  if (!tienePermiso('qr.ver')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
          <p className="text-gray-600">
            No tienes permiso para acceder a esta funcionalidad (qr.ver).
          </p>
        </div>
      </div>
    )
  }


  const handleScanSuccess = async (qrData: string) => {
    setIsScannerOpen(false)
    await locateQR(qrData)
  }

  const handleManualInput = async (qrData: string) => {
    setIsManualInputOpen(false)
    await locateQR(qrData)
  }

  const handleCloseResult = () => {
    clearResult()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            📍 Localizador de Espacios
          </h1>
          <p className="text-lg text-gray-600">
            Escanea el código QR de la reserva para ver dónde está ubicado el espacio en el plano del camping.
          </p>
        </div>

        {/* Tarjetas de información */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Escanear */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-3xl mb-3">📷</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              1. Escanear QR
            </h3>
            <p className="text-gray-600 text-sm">
              Presiona el botón para abrir la cámara y apunta al código QR de la reserva.
            </p>
          </div>

          {/* Localizar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-3xl mb-3">🗺️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              2. Ver Plano
            </h3>
            <p className="text-gray-600 text-sm">
              El sistema muestra el plano del camping con la ubicación del espacio marcada.
            </p>
          </div>

          {/* Navegar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-3xl mb-3">👤</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              3. Encontrar Huésped
            </h3>
            <p className="text-gray-600 text-sm">
              Usa el plano para encontrar y orientarte hacia el espacio del huésped.
            </p>
          </div>
        </div>

        {/* Botones de escaneo e ingreso manual */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          <button
            onClick={() => setIsScannerOpen(true)}
            className="px-8 py-4 rounded-lg font-semibold text-white text-lg transition-all duration-200 hover:shadow-lg"
            style={{
              backgroundColor: colors.primary,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark
              e.currentTarget.style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            📱 Abrir Escáner QR
          </button>
          <button
            onClick={() => setIsManualInputOpen(true)}
            className="px-8 py-4 rounded-lg font-semibold text-white text-lg transition-all duration-200 hover:shadow-lg"
            style={{
              backgroundColor: colors.primaryDark,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9'
              e.currentTarget.style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            ✏️ Ingreso Manual
          </button>
        </div>

        {/* Información adicional */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            💡 Información Importante
          </h3>
          <ul className="text-blue-800 space-y-2 text-sm">
            <li>✓ El código QR está incluido en el correo de confirmación de la reserva</li>
            <li>✓ El plano del camping muestra la ubicación exacta del espacio</li>
            <li>✓ El espacio está marcado con un color destacado en el plano</li>
            <li>✓ Puedes ingresar manualmente el código si tienes problemas con la cámara</li>
            <li>✓ Solo se muestran espacios de reservas válidas y confirmadas</li>
          </ul>
        </div>

        {/* Estado de carga */}
        {isLocating && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div
              className="animate-spin w-12 h-12 border-4 border-t-transparent rounded-full mx-auto mb-4"
              style={{ borderColor: `${colors.primary}40`, borderTopColor: 'transparent' }}
            ></div>
            <p className="text-gray-600 font-medium">Localizando espacio...</p>
          </div>
        )}
      </div>

      {/* Modales */}
      <QRScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScanSuccess={handleScanSuccess}
      />

      <ManualQRInput
        isOpen={isManualInputOpen}
        onClose={() => setIsManualInputOpen(false)}
        onSubmit={handleManualInput}
        isLoading={isLocating}
      />

      <QRLocatorResult
        data={locatorResult}
        isLoading={isLocating}
        onClose={handleCloseResult}
      />
    </div>
  )
}

export default QRLocalizadorPage
